import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { sendSubscribeWebhook } from '../../utils/webhookUtils';

const NAV_LINKS = [
  { to: '/', label: 'Home', color: 'text-white', activeColor: 'text-white', hoverColor: 'hover:text-white', glowColor: 'from-white', accentColor: '#ffffff' },
  { to: '/gaming', label: 'Gaming', color: 'text-purple-400', activeColor: 'text-purple-400', hoverColor: 'hover:text-purple-400', glowColor: 'from-purple-500', accentColor: '#a855f7' },
  { to: '/streaming', label: 'Streaming', color: 'text-cyan-400', activeColor: 'text-cyan-400', hoverColor: 'hover:text-cyan-400', glowColor: 'from-cyan-500', accentColor: '#22d3ee' },
  { to: '/office', label: 'Office', color: 'text-emerald-400', activeColor: 'text-emerald-400', hoverColor: 'hover:text-emerald-400', glowColor: 'from-emerald-500', accentColor: '#34d399' },
  { to: '/articles', label: 'Articles', color: 'text-white', activeColor: 'text-white', hoverColor: 'hover:text-white', glowColor: 'from-white', accentColor: '#ffffff' },
  { to: '/about', label: 'About', color: 'text-white', activeColor: 'text-white', hoverColor: 'hover:text-white', glowColor: 'from-white', accentColor: '#ffffff' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [subEmail, setSubEmail] = useState('');
  const [subStatus, setSubStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const subscribeRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Close subscribe popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (subscribeRef.current && !subscribeRef.current.contains(e.target as Node)) {
        setSubscribeOpen(false);
      }
    };
    if (subscribeOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [subscribeOpen]);

  // Close subscribe popover on route change
  useEffect(() => {
    setSubscribeOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  const handleHeaderSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subEmail) return;
    setSubStatus('submitting');
    try {
      const [res] = await Promise.allSettled([
        fetch('https://readdy.ai/api/form/d6t4n6vcrcujsk0v13d0', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ email: subEmail, source: 'Header Signup' }).toString(),
        }),
        sendSubscribeWebhook({
          email: subEmail,
          source: 'Header Signup',
          page_url: window.location.href,
        }),
      ]);
      const localOk = res.status === 'fulfilled' && (res.value as Response).ok;
      if (localOk) {
        setSubStatus('success');
        setSubEmail('');
        setTimeout(() => { setSubStatus('idle'); setSubscribeOpen(false); }, 3000);
      } else {
        setSubStatus('error');
      }
    } catch {
      setSubStatus('error');
    }
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileOpen
          ? 'bg-black/90 backdrop-blur-lg border-b border-cyan-500/20 shadow-lg shadow-cyan-500/10'
          : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">

            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 cursor-pointer group flex-shrink-0">
              <div className="relative w-11 h-11 flex items-center justify-center flex-shrink-0">
                <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-cyan-500/40 to-purple-600/40 blur-xl group-hover:blur-2xl transition-all duration-500 scale-110"></div>
                <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-cyan-400/20 to-purple-500/20 blur-md group-hover:from-cyan-400/30 group-hover:to-purple-500/30 transition-all duration-300"></div>
                <div
                  className="relative w-11 h-11 flex items-center justify-center rounded-xl overflow-hidden transition-all duration-300 border border-white/10 group-hover:border-cyan-400/40"
                  style={{
                    background: 'linear-gradient(135deg, rgba(6,182,212,0.12) 0%, rgba(0,0,0,0.7) 50%, rgba(147,51,234,0.12) 100%)',
                    backdropFilter: 'blur(12px)',
                    boxShadow: '0 0 0 1px rgba(6,182,212,0.15), 0 0 16px rgba(6,182,212,0.25), 0 0 32px rgba(147,51,234,0.15)',
                  }}
                >
                  <div className="absolute inset-0 rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, transparent 60%)' }}></div>
                  <div className="absolute inset-px rounded-[10px] border border-white/5"></div>
                  <i className="ri-cpu-line text-lg relative z-10" style={{ background: 'linear-gradient(135deg, #22d3ee, #a855f7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', filter: 'drop-shadow(0 0 6px rgba(6,182,212,0.6))' }}></i>
                </div>
              </div>
              <div className="flex flex-col justify-center gap-0.5">
                <div className="flex items-baseline gap-0.5">
                  <span className="text-lg font-black tracking-tight text-emerald-400 group-hover:text-emerald-300 transition-colors duration-200" style={{ textShadow: '0 0 12px rgba(52,211,153,0.6)', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.02em' }}>WORK</span>
                  <span className="text-emerald-500/40 text-xs font-light mx-0.5">·</span>
                  <span className="text-lg font-black tracking-tight text-purple-400 group-hover:text-purple-300 transition-colors duration-200" style={{ textShadow: '0 0 12px rgba(192,132,252,0.6)', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.02em' }}>PLAY</span>
                  <span className="text-cyan-500/40 text-xs font-light mx-0.5">·</span>
                  <span className="text-lg font-black tracking-tight text-cyan-400 group-hover:text-cyan-300 transition-colors duration-200" style={{ textShadow: '0 0 12px rgba(34,211,238,0.6)', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.02em' }}>SAVE</span>
                </div>
                <div className="flex items-center gap-1 mt-0.5">
                  <div className="h-px flex-1 bg-gradient-to-r from-emerald-500/60 via-purple-500/60 to-cyan-500/60"></div>
                  <span className="text-[9px] uppercase tracking-[0.2em] font-black" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#c084fc', textShadow: '0 0 8px rgba(192,132,252,0.9), 0 0 16px rgba(192,132,252,0.5)', letterSpacing: '0.22em' }}>Tech Reviews</span>
                  <div className="h-px flex-1 bg-gradient-to-r from-cyan-500/60 via-purple-500/60 to-emerald-500/60"></div>
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`text-sm uppercase tracking-wider transition-colors duration-200 relative group whitespace-nowrap cursor-pointer ${
                    isActive(link.to) ? link.activeColor : `text-gray-400 ${link.hoverColor}`
                  }`}
                >
                  {link.label}
                  <span className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r ${link.glowColor} to-transparent transition-all duration-300 ${
                    isActive(link.to) ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}></span>
                </Link>
              ))}
            </div>

            {/* Right: Subscribe + Hamburger */}
            <div className="flex items-center gap-3">
              <div className="relative hidden sm:block" ref={subscribeRef}>
                <button
                  onClick={() => setSubscribeOpen((prev) => !prev)}
                  className="px-4 py-1.5 sm:px-6 sm:py-2 border border-cyan-500 text-cyan-400 rounded-lg hover:bg-cyan-500/10 hover:shadow-lg hover:shadow-cyan-500/20 transition-all duration-200 text-xs sm:text-sm uppercase tracking-wider whitespace-nowrap cursor-pointer flex-shrink-0"
                >
                  Subscribe
                </button>

                {/* Subscribe Popover */}
                {subscribeOpen && (
                  <div className="absolute top-full right-0 mt-3 w-72 rounded-xl border border-cyan-500/25 bg-black/95 backdrop-blur-xl z-50 overflow-hidden"
                    style={{ boxShadow: '0 0 40px rgba(6,182,212,0.15), 0 8px 32px rgba(0,0,0,0.6)' }}
                  >
                    <div className="h-px w-full bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
                    <div className="p-5">
                      <p className="text-white font-bold text-sm mb-1">Get weekly tech picks</p>
                      <p className="text-gray-500 text-xs mb-4">New reviews and buying guides, straight to your inbox.</p>

                      {subStatus === 'success' ? (
                        <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                            <i className="ri-checkbox-circle-fill text-emerald-400 text-base"></i>
                          </div>
                          <p className="text-emerald-300 text-sm font-medium">Thanks for subscribing. Check your email.</p>
                        </div>
                      ) : (
                        <form onSubmit={handleHeaderSubscribe} className="space-y-3">
                          <input type="hidden" name="source" value="Header Signup" />
                          <div className="relative">
                            <input
                              type="email"
                              name="email"
                              value={subEmail}
                              onChange={(e) => setSubEmail(e.target.value)}
                              placeholder="Enter your email"
                              required
                              className="w-full px-4 py-2.5 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500/60 transition-all duration-200 text-sm pr-12"
                            />
                            <button
                              type="submit"
                              disabled={subStatus === 'submitting'}
                              className="absolute right-1.5 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-md text-white hover:shadow-lg hover:shadow-cyan-500/30 transition-all duration-200 disabled:opacity-50 cursor-pointer"
                            >
                              {subStatus === 'submitting' ? (
                                <i className="ri-loader-4-line animate-spin text-xs"></i>
                              ) : (
                                <i className="ri-send-plane-fill text-xs"></i>
                              )}
                            </button>
                          </div>
                          {subStatus === 'error' && (
                            <p className="text-red-400 text-xs">Something went wrong. Please try again.</p>
                          )}
                          <p className="text-xs text-gray-600">No spam. Unsubscribe anytime.</p>
                        </form>
                      )}
                    </div>
                    <div className="h-px w-full bg-gradient-to-r from-transparent via-purple-500/20 to-transparent" />
                  </div>
                )}
              </div>

              {/* Hamburger — mobile only */}
              <button
                onClick={() => setMobileOpen((prev) => !prev)}
                className="md:hidden flex flex-col items-center justify-center w-10 h-10 rounded-lg border border-white/10 bg-white/5 cursor-pointer transition-all duration-200 hover:border-cyan-500/40 hover:bg-cyan-500/10 flex-shrink-0"
                aria-label="Toggle menu"
              >
                <span className={`block w-5 h-0.5 bg-white transition-all duration-300 ${mobileOpen ? 'rotate-45 translate-y-1.5' : ''}`}></span>
                <span className={`block w-5 h-0.5 bg-white mt-1.5 transition-all duration-300 ${mobileOpen ? 'opacity-0 scale-x-0' : ''}`}></span>
                <span className={`block w-5 h-0.5 bg-white mt-1.5 transition-all duration-300 ${mobileOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></span>
              </button>
            </div>

          </div>
        </div>
      </nav>

      {/* Mobile Drawer Overlay */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-all duration-300 ${mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
        onClick={() => setMobileOpen(false)}
      ></div>

      {/* Mobile Drawer Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-72 z-50 md:hidden flex flex-col transition-transform duration-300 ease-in-out ${mobileOpen ? 'translate-x-0' : 'translate-x-full'}`}
        style={{
          background: 'linear-gradient(180deg, rgba(5,5,15,0.98) 0%, rgba(10,5,20,0.99) 100%)',
          borderLeft: '1px solid rgba(6,182,212,0.15)',
        }}
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
          <span className="text-xs uppercase tracking-[0.25em] text-gray-500 font-semibold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Navigation</span>
          <button
            onClick={() => setMobileOpen(false)}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-white/10 text-gray-400 hover:text-white hover:border-cyan-500/40 transition-all duration-200 cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Nav Links */}
        <nav className="flex flex-col px-4 py-6 gap-1 flex-1 overflow-y-auto">
          {NAV_LINKS.map((link, idx) => {
            const active = isActive(link.to);
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-200 cursor-pointer group ${
                  active
                    ? 'bg-white/5 border border-white/10'
                    : 'hover:bg-white/5 border border-transparent hover:border-white/8'
                }`}
                style={{
                  animationDelay: `${idx * 40}ms`,
                }}
              >
                {/* Active indicator bar */}
                <span
                  className={`w-1 h-6 rounded-full flex-shrink-0 transition-opacity duration-200 ${active ? 'opacity-100' : 'opacity-0 group-hover:opacity-40'}`}
                  style={{ background: link.accentColor, boxShadow: active ? `0 0 8px ${link.accentColor}80` : 'none' }}
                ></span>
                <span
                  className={`text-sm uppercase tracking-widest font-bold transition-colors duration-200 ${
                    active ? link.activeColor : `text-gray-400 group-hover:${link.color}`
                  }`}
                  style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.12em' }}
                >
                  {link.label}
                </span>
                {active && (
                  <i className="ri-arrow-right-s-line text-gray-500 ml-auto text-base"></i>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Drawer Footer — Subscribe CTA */}
        <div className="px-6 py-6 border-t border-white/5">
          <button
            onClick={() => {
              setMobileOpen(false);
              setTimeout(() => {
                const emailInput = document.querySelector('#newsletter-form input[type="email"]') as HTMLElement;
                const newsletterForm = document.getElementById('newsletter-form');
                const target = emailInput || newsletterForm;
                if (target) {
                  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  setTimeout(() => { if (emailInput) emailInput.focus(); }, 600);
                }
              }, 350);
            }}
            className="w-full py-3 border border-cyan-500/60 text-cyan-400 rounded-xl text-xs uppercase tracking-widest font-bold hover:bg-cyan-500/10 transition-all duration-200 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.18em' }}
          >
            Subscribe to Newsletter
          </button>
          <p className="text-center text-gray-600 text-[10px] mt-3 tracking-wider">
            workplaysave.com · Tech Reviews
          </p>
        </div>
      </div>
    </>
  );
}
